import { Navigate, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useIsAuthenticated } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import { storageHelper } from "./storageHelper";
import { parameterHelper } from './parameterHelper';
import Spinner from "../components/spinner/Spinner";
import { Grid, Box, useTheme } from "@mui/material";
import { useEffect } from "react";
import { startReload } from "../redux/permissionsSlice";
import { getAuthenticatedHeaders } from "../fetch/fetchHeaders";
import { httpResponseToJSON } from "./fetchHelpers";
import { extractUidDealerNumber, UserPermissionRoute } from "./routeHelpers";

export function ServiceLogPermissionRoute({ children }) {
    const theme = useTheme();
    const litListMatch = useMatch('/literature');
    const navigate = useNavigate();
    const isAuthenticated = useIsAuthenticated();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const { activationKey, uid, lang } = parameterHelper.getUrlParameters({ activationKey: null, uid: null, lang: null });
    const { isLoaded, isRecognized, accountNumber, language, hasServiceLog } = useSelector((state) => state.permissions);
    const requiresMercNetLinkIn = useSelector((state) => state.permissions.requiresMercNetLinkIn);

    const mercNetDealer = extractUidDealerNumber(uid);

    useEffect(() => {
        const persistedMercNetDealer = mercNetDealer || storageHelper.getMercNetDealer();
        if (isLoaded && isRecognized && !!persistedMercNetDealer && requiresMercNetLinkIn) {
            // We are changing dealer number so we'll update the server and force a reload
            const url = `/user/AssociateFromMercNet/${persistedMercNetDealer}?language=${language}`;
            getAuthenticatedHeaders().then((headers) => {
                return fetch(url, { headers })
                    .then(response => httpResponseToJSON(response, 'Error loading data'/*, showError*/))
                    .then((data) => {
                        if (data.status == 4 || data.status == 6) {
                            dispatch(startReload())
                            navigate('/literature');
                        } else {
                            // Theoretical impossible but error
                            // If we do get an error something might be wrong with the activation
                            // key so clear it out so the user isn't stuck
                            storageHelper.setMercNetDealer('');
                            storageHelper.setMercNetLanguage('');
                            throw new Error('Error validating user');
                        }
                    });
            });
        }
    }, [isLoaded, isRecognized, accountNumber]);

    if (!!mercNetDealer && mercNetDealer !== storageHelper.getMercNetDealer()) {
        storageHelper.setMercNetDealer(mercNetDealer);
        storageHelper.setMercNetLanguage(lang);
    }

    if (isAuthenticated && !isLoaded) {
        // user is not authenticated
        return (
            <Grid container>
                <Grid item xs={12}>
                    {/* For literature list page to make sure it looks like it's loading faster
                    we are going to put a sub menu placeholder */}
                    {!!litListMatch ? (
                        <Box
                            display='flex'
                            justifyContent='center'
                            alignItems='center'
                        >
                            <Grid container style={{ height: theme.subBar.height, background: '#FFF', width: '100%' }}>
                            </Grid>
                        </Box>
                    ) : null}
                    <Box
                        display='flex'
                        justifyContent='center'
                        alignItems='center'
                    >
                        <Spinner />
                    </Box>
                </Grid>
            </Grid>
        );
    }

    if (isAuthenticated && isLoaded && !isRecognized) {
        if (!!activationKey) {
            storageHelper.setActivationKey(activationKey);
        }

        // user is not authenticated
        return <Navigate to={`/literature/link?redirect=${encodeURIComponent(pathname)}`} />;
    }

    if (isAuthenticated && isLoaded && isRecognized && requiresMercNetLinkIn) {
        return <Navigate to={`/literature/mercnet`} />;
    }

    if (isLoaded && isRecognized && !!storageHelper.getActivationKey()) {
        // We are recognized so we can clear this out
        storageHelper.setActivationKey('');
    }

    if(isAuthenticated && isRecognized && isLoaded && !hasServiceLog) {
        return <Navigate to={`/`} />;
    }

    return (
        <UserPermissionRoute>
            {children}
        </UserPermissionRoute>
    );
}